/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import {
    ActionButton,
    IButtonProps,
    IButtonStyles,
    ICalloutContentStyleProps,
    ICalloutContentStyles,
    IDropdownOption,
    Icon,
    IconButton,
    Stack
} from '@fluentui/react';
import { classNamesFunction } from '@fluentui/utilities';
import { Property } from 'csstype';
import { observer } from 'mobx-react';
import * as React from 'react';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logFreeAccountCreation } from 'src/core/services/telemetry/eventsNamesTemplate';
import { AzureCsAccountKind, AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { AzureSubscriptionStore } from 'src/core/stores/AzureSubscriptionStore';
import { AzureTenantStore } from 'src/core/stores/AzureTenantStore';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { FormValidators } from 'src/core/utils/formValidators';
import { azureResourceModalsMessages, getModalText } from 'src/modules/azure/components/azureResourceModalsMessages';
import styled from 'styled-components';

export type AzureCsAccountSelectModalV2Props = BasicModalProps<{}> & {
    selectedTenantId: string;
    areTenantsLoading: boolean;
    selectedResourceId: string;
    tenants: AzureTenantStore[];
    areResourcesLoading: boolean;
    onReloadResources: () => void;
    selectedSubscriptionId: string;
    areSubscriptionsLoading: boolean;
    resources: AzureCsAccountStore[];
    serviceTarget: AzureCsAccountKind;
    onCreateResourceClicked?: () => void;
    resourceTypeOptions: IDropdownOption[];
    subscriptions: AzureSubscriptionStore[];
    isResourceTypeDescriptionHidden?: boolean;
    showResourcesTypeDropDown?: boolean;
    onChangeResourceType: (newResourceType: AzureCsAccountKind) => void;
    onChangeResource: (newResourceId: string, hasError: boolean) => void;
    onChangeSubscription: (newSubscriptionId: string, hasError: boolean) => void;
    resourcesValidators?: ((newSubscriptionId: string) => ReactIntl.FormattedMessage.MessageDescriptor)[];
    subscriptionsValidators?: ((newSubscriptionId: string) => ReactIntl.FormattedMessage.MessageDescriptor)[];
};

const modalTheme = getScopedTheme('AzureCsAccountSelectModal');
const buttonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    root: { alignSelf: 'flex-start', color: modalTheme.buttonColor }
});
const dropdownStyles = classNamesFunction<ICalloutContentStyleProps, ICalloutContentStyles>()({
    calloutMain: { maxHeight: '300px !important' }
});

const { styleSpan } = getStylistV2('AzureCsAccountSelectModal');
const AccountLabel = styleSpan(
    'AccountLabel',
    (theme, cssVars) => ({
        marginRight: cssVars.marginRight,
        fontWeight: theme.accountInfoFontWeight as Property.FontWeight
    }),
    {
        cssVars: { marginRight: '' },
        cssVarsSetter: ({ marginRight }: { marginRight: string }) => ({ marginRight })
    }
);

const ExpandIcon = styled(Icon)({
    fontSize: '14px'
});

export const AzureCsAccountSelectModalV2 = observer((props: AzureCsAccountSelectModalV2Props) => {
    const {
        tenants,
        resources,
        subscriptions,
        serviceTarget,
        selectedTenantId,
        onChangeResource,
        onReloadResources,
        areTenantsLoading,
        selectedResourceId,
        resourceTypeOptions,
        areResourcesLoading,
        onChangeSubscription,
        onChangeResourceType,
        selectedSubscriptionId,
        areSubscriptionsLoading,
        onCreateResourceClicked,
        showResourcesTypeDropDown = true,
        isResourceTypeDescriptionHidden = true,
        resourcesValidators = [id => FormValidators.createRequiredValidator()(id)?.messageDescriptor],
        subscriptionsValidators = [id => FormValidators.createRequiredValidator()(id)?.messageDescriptor]
    } = props;
    const { localize } = useLocalization();
    const { dispatcher, rootStore } = useGlobalState();
    const { modalInstructions, createNewAccountButton, learnMoreLink, azureResourceCreationLink } = getModalText(serviceTarget, 'select');

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canChangeAppSettingsAndDetails } = mapUserRoleToUserActions(userRole);

    const [resourcesErrorMessage, setResourcesErrorMessage] = React.useState('');
    const [subscriptionsErrorMessage, setSubscriptionsErrorMessage] = React.useState('');

    let csAccountLabel = '';
    let csAccountPlaceholder = '';
    const isReadyToSubmit = !(Boolean(subscriptionsErrorMessage) || Boolean(resourcesErrorMessage)) && Boolean(selectedResourceId);
    const resourceSelected = resources.find(a => a.id === selectedResourceId);

    const resourcesOptions = React.useMemo(
        () =>
            resources.map(item => ({
                key: item.id,
                text: item.name,
                data: { secondaryText: `(${item.region}, ${item.sku.code})` }
            })),
        [resources]
    );

    const areThereResources = React.useMemo(() => resources.some(resource => resource.kind === serviceTarget), [resources]);
    const showCreateResourceButton = !areResourcesLoading && selectedSubscriptionId;

    switch (serviceTarget) {
        case 'LUIS.Authoring':
            csAccountLabel = localize(azureResourceModalsMessages.csAuthoringAccountLabel);
            csAccountPlaceholder = localize(azureResourceModalsMessages.csAccountPlaceholderAuthoring);
            break;
        case 'TextAnalytics':
        case 'TextTranslation':
        case 'CognitiveServices':
            csAccountLabel = localize(azureResourceModalsMessages.csResourceAccountLabel);
            csAccountPlaceholder = localize(azureResourceModalsMessages.azureResourcePlaceHolder);
            break;
        case 'LUIS':
        default:
            csAccountLabel = localize(azureResourceModalsMessages.csPredictionAccountLabel);
            csAccountPlaceholder = localize(azureResourceModalsMessages.predictionCsAccountPlaceholder);
    }

    const onSubscriptionChange = (newSubscriptionId: string) => {
        const errors: ReactIntl.FormattedMessage.MessageDescriptor[] = subscriptionsValidators
            .reduce((acc, v) => [...acc, v(newSubscriptionId)], [])
            .filter(Boolean);
        const hasError = Boolean(errors.length);

        if (hasError) {
            setSubscriptionsErrorMessage(localize(errors[0]));
        } else if (subscriptionsErrorMessage) {
            setSubscriptionsErrorMessage('');
        }

        onChangeSubscription(newSubscriptionId, hasError);
    };

    const onResourceChange = (newResourceId: string) => {
        const errors: ReactIntl.FormattedMessage.MessageDescriptor[] = resourcesValidators
            .reduce((acc, v) => [...acc, v(newResourceId)], [])
            .filter(Boolean);
        const hasError = Boolean(errors.length);

        if (hasError) {
            setResourcesErrorMessage(localize(errors[0]));
        } else if (subscriptionsErrorMessage) {
            setResourcesErrorMessage('');
        }

        onChangeResource(newResourceId, hasError);
    };

    const onRenderResourceTypeOption = (option: IDropdownOption) =>
        option && (
            <LuisTooltip content={option.disabled && localize(azureResourceModalsMessages.resourceNotSupported)}>
                <span>{option.text}</span>
            </LuisTooltip>
        );

    return (
        <BasicModal
            {...props}
            valid={isReadyToSubmit}
            activitySource="azureResourceSelectModal"
            dialogContentProps={{ title: localize(azureResourceModalsMessages.azureResourceModalTitle) }}>
            <Stack tokens={{ childrenGap: 20 }}>
                <LuisText>
                    {modalInstructions}&nbsp;
                    <ExternalLink href={learnMoreLink}>{localize(azureResourceModalsMessages.resourceLearnMore)}</ExternalLink>
                </LuisText>
                <Stack tokens={{ childrenGap: 20 }}>
                    <Stack tokens={{ childrenGap: 2 }}>
                        <LuisDropdown
                            required
                            maxHeight={300}
                            isLoading={areTenantsLoading}
                            selectedKey={selectedTenantId}
                            data-automation-id="tenantsDropdown"
                            label={localize(azureResourceModalsMessages.tenantLabel)}
                            options={tenants.map(item => ({ key: item.id, text: item.name }))}
                            onChange={(_, opt) => dispatcher.dispatch('switchAzureTenantInSession', { id: opt.key as string })}
                        />
                        <LuisText variant="small">{localize(azureResourceModalsMessages.tenantDescription)}</LuisText>
                    </Stack>

                    <LuisDropdown
                        required
                        isLoading={areSubscriptionsLoading}
                        selectedKey={selectedSubscriptionId}
                        errorMessage={subscriptionsErrorMessage}
                        calloutProps={{ styles: dropdownStyles }}
                        data-automation-id="subscriptionsDropdown"
                        onChange={(_, opt) => onSubscriptionChange(opt.key as string)}
                        label={localize(azureResourceModalsMessages.subscriptionLabel)}
                        options={subscriptions.map(item => ({ key: item.id, text: item.name }))}
                        placeholder={localize(azureResourceModalsMessages.subscriptionPlaceholder)}
                        onRenderLabel={(labelProps, defaultLabelRenderer) => (
                            <Stack tokens={{ childrenGap: 5, padding: '0 0 5px 0' }}>
                                {defaultLabelRenderer(labelProps)}
                                <LuisText>
                                    {localize(azureResourceModalsMessages.selectExistingSubscriptionLabel)}&nbsp;
                                    <ExternalLink href="https://aka.ms/azure-freeaccount" onClick={() => logFreeAccountCreation()}>
                                        {localize(azureResourceModalsMessages.createFreeAccount)}
                                    </ExternalLink>
                                    &nbsp;
                                    {localize(azureResourceModalsMessages.selectExistingSubscriptionLabelPart2)}
                                </LuisText>
                            </Stack>
                        )}
                    />

                    {showResourcesTypeDropDown && (
                        <Stack tokens={{ childrenGap: 2 }}>
                            <LuisDropdown
                                required
                                isSearchEnabled={false}
                                data-automation-id="resourcesKindDropDown"
                                selectedKey={serviceTarget}
                                onChange={(_, kind) => onChangeResourceType(kind.key as AzureCsAccountKind)}
                                label={localize(azureResourceModalsMessages.resourceType)}
                                options={resourceTypeOptions}
                                onRenderOption={onRenderResourceTypeOption}
                            />
                            {!isResourceTypeDescriptionHidden && (
                                <LuisText variant="small">{localize(azureResourceModalsMessages.resourceTypeDescription)}</LuisText>
                            )}
                        </Stack>
                    )}

                    <Stack tokens={{ childrenGap: 12 }}>
                        <Stack horizontal verticalAlign="end" horizontalAlign="space-between">
                            <LuisDropdown
                                required
                                label={csAccountLabel}
                                options={resourcesOptions}
                                ariaLabel={csAccountLabel}
                                isLoading={areResourcesLoading}
                                selectedKey={selectedResourceId}
                                placeholder={csAccountPlaceholder}
                                styles={{ root: { width: '95%' } }}
                                aria-labelledby={selectedResourceId}
                                errorMessage={resourcesErrorMessage}
                                data-automation-id="csAccountsDropdown"
                                calloutProps={{ styles: dropdownStyles }}
                                onChange={(_, item) => onResourceChange(item.key as string)}
                                disabled={Boolean(subscriptionsErrorMessage) || !Boolean(selectedSubscriptionId)}
                                onRenderLabel={(labelProps, defaultLabelRenderer) => (
                                    <Stack tokens={{ childrenGap: 5, padding: '0 0 5px 0' }}>
                                        {defaultLabelRenderer(labelProps)}
                                        <LuisText>{localize(azureResourceModalsMessages.selectExistingResourceLabel)}</LuisText>
                                    </Stack>
                                )}
                            />

                            <IconButton
                                onClick={onReloadResources}
                                iconProps={{ iconName: 'Refresh', ariaLabel: 'Refresh' }}
                                disabled={areResourcesLoading || !selectedSubscriptionId}
                                ariaLabel={'Refresh'}
                            />
                        </Stack>

                        {resourceSelected && (
                            <Stack tokens={{ childrenGap: 8 }}>
                                <LuisText variant="small">
                                    <AccountLabel customProps={{ marginRight: '50px' }}>
                                        {localize(azureResourceModalsMessages.pricingTier)}
                                    </AccountLabel>
                                    <LuisText fontWeight={400}>
                                        {resourceSelected.sku.name} ({resourceSelected.sku.code})
                                    </LuisText>
                                </LuisText>

                                <LuisText variant="small">
                                    <AccountLabel customProps={{ marginRight: '12px' }}>
                                        {localize(azureResourceModalsMessages.managedIdentity)}
                                    </AccountLabel>
                                    <LuisText fontWeight={400}>
                                        {resourceSelected.isIdentityEnabled
                                            ? localize(azureResourceModalsMessages.enabled)
                                            : localize(azureResourceModalsMessages.disabled)}
                                    </LuisText>
                                </LuisText>
                            </Stack>
                        )}

                        {showCreateResourceButton &&
                            (FeatureToggles.LuisResourceCreation || (serviceTarget !== 'LUIS' && serviceTarget !== 'LUIS.Authoring')) &&
                            (onCreateResourceClicked && areThereResources ? (
                                <RBACTooltip
                                    displayedUserRole={displayedUserRole}
                                    hidden={canChangeAppSettingsAndDetails}
                                    data-automation-id="createNewAccountButtonTooltip"
                                    componentSurroundedByToolTip={
                                        <ActionButton
                                            styles={buttonStyles}
                                            onClick={onCreateResourceClicked}
                                            data-automation-id="createAccountButton"
                                            disabled={!canChangeAppSettingsAndDetails}>
                                            {createNewAccountButton}
                                        </ActionButton>
                                    }
                                />
                            ) : (
                                <RBACTooltip
                                    displayedUserRole={displayedUserRole}
                                    hidden={canChangeAppSettingsAndDetails}
                                    data-automation-id="createNewAccountInAzureButtonTooltip"
                                    componentSurroundedByToolTip={
                                        serviceTarget === 'LUIS.Authoring' || serviceTarget === 'LUIS' ? (
                                            <ActionButton
                                                styles={buttonStyles}
                                                onClick={onCreateResourceClicked}
                                                data-automation-id="createAccountButton"
                                                disabled={!canChangeAppSettingsAndDetails}>
                                                {createNewAccountButton}
                                            </ActionButton>
                                        ) : (
                                            <ExternalLink
                                                disabled={!canChangeAppSettingsAndDetails}
                                                data-automation-id="createNewAccountInAzureButton"
                                                href={azureResourceCreationLink}>
                                                <Stack horizontal verticalAlign="baseline">
                                                    {`${createNewAccountButton} ${localize(azureResourceModalsMessages.inAzurePortal)}`}
                                                    &nbsp;
                                                    <ExpandIcon iconName="MiniExpand" />
                                                </Stack>
                                            </ExternalLink>
                                        )
                                    }
                                />
                            ))}
                    </Stack>
                </Stack>
            </Stack>
        </BasicModal>
    );
});
